import React, { useEffect, useMemo } from 'react'
import { Alert, Spinner, Table } from 'react-bootstrap'
import { GhgCalculatorNavigation } from './GhgCalculatorNavigation'

import './GhgResultStep.scss'
import { formatNumber } from 'utils/formatting'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSave } from '@fortawesome/free-solid-svg-icons'
import { Button } from './Button'
import { useGhgCalculatorFormContext } from './use-ghg-calculator-form'
import { GhgCalculationResult } from 'api/models'
import { parseErrorMessages } from 'utils/error'
import { FarmDetailState } from './ghg-calculator.state'
import Tippy from '@tippyjs/react'
import { TooltipIcon } from './base/TooltipIcon'

const CO2 = (
  <>
    CO<sub>2</sub>
  </>
)
const CH4 = (
  <>
    CH<sub>4</sub>
  </>
)
const N2O = (
  <>
    N<sub>2</sub>O
  </>
)
const CO2e = (
  <>
    CO<sub>2</sub>-e
  </>
)

export const GhgResultStep = () => {
  const {
    saveCalculation,
    previewResultResponse,
    saveCalculationResponse,
    previewResult,
    farmDetail,
    negativeLossErrors,
  } = useGhgCalculatorFormContext()

  useEffect(() => {
    previewResult()
  }, [previewResult])

  const submissionErrors = useMemo(() => {
    if (saveCalculationResponse.error) {
      return parseErrorMessages(saveCalculationResponse.error)
    }
    return []
  }, [saveCalculationResponse.error])

  const previewErrors = useMemo(() => {
    if (previewResultResponse.error) {
      return parseErrorMessages(previewResultResponse.error)
    }
    return []
  }, [previewResultResponse.error])

  return previewResultResponse.loading ? (
    <div className="text-center">
      <Spinner animation="border" role="status">
        <span className="sr-only">Loading...</span>
      </Spinner>
    </div>
  ) : (
    <div>
      {previewErrors.length ? (
        <Alert variant="danger" data-testid="preview-errors">
          <h4>Error calculating results</h4>
          <ul className="mt-2">
            {previewErrors.map((errorMsg, idx) => (
              <li className="text-danger" key={idx}>
                {errorMsg}
              </li>
            ))}
          </ul>
        </Alert>
      ) : null}

      {negativeLossErrors ? (
        <p className="text-danger" data-testid="negative-loss-error-message">
          An error remains in your stock reconciliation. It is recommended you review your{' '}
          <strong>Livestock movements</strong> to improve the accuracy of your results.
        </p>
      ) : null}

      {previewResultResponse.data ? (
        <ResultPreview farmDetail={farmDetail} result={previewResultResponse.data} />
      ) : null}

      {submissionErrors.length ? (
        <Alert variant="danger" data-testid="submission-errors">
          <h4>Erorrs occured during form submission</h4>
          <ul className="mt-2">
            {submissionErrors.map((errorMsg, idx) => (
              <li className="text-danger" key={idx}>
                {errorMsg}
              </li>
            ))}
          </ul>
        </Alert>
      ) : null}

      <GhgCalculatorNavigation>
        <Button
          loading={saveCalculationResponse.loading}
          onClick={saveCalculation}
          variant="primary"
          className="mr-3 btn-lg"
        >
          <FontAwesomeIcon icon={faSave} className="btn-icon-left" />
          Save results
        </Button>
      </GhgCalculatorNavigation>
    </div>
  )
}

export const ResultPreview = ({
  farmDetail,
  result,
}: {
  result: GhgCalculationResult
  farmDetail: FarmDetailState
}) => {
  const {
    farm_emissions,
    benchmark_emissions,
    deforestation_emissions,
    vegetation_offsets,
    totals,
  } = result
  const { livestock_emissions, fertiliser_and_lime_use } = farm_emissions
  return (
    <>
      <div className="row mt-4">
        <div className="col-12">
          <h3 className="text-center mb-5">
            Greenhouse Gas Emissions Calculation for <strong>{farmDetail.name}</strong>
          </h3>
          <Table bordered className="table-ghg-results">
            <thead>
              <tr>
                <th colSpan={3} className="w-col-md-8">
                  <strong>Farm emissions</strong>
                </th>
                <th colSpan={3} className="table-cell-space-left">
                  <strong>Kilograms of greenhouse gases</strong>
                </th>
              </tr>
              <tr>
                <th colSpan={2}>
                  <strong>Source</strong>
                </th>
                <th className="text-center w-20">
                  <strong>
                    Kilograms of Carbon dioxide equivalents {CO2e}
                    <sup>*</sup>
                  </strong>
                </th>
                <th className="text-center table-cell-space-left">
                  <strong>Carbon dioxide {CO2}</strong>
                </th>
                <th className="text-center">
                  <strong>Methane {CH4}</strong>
                </th>
                <th className="text-center">
                  <strong>Nitrous oxide {N2O}</strong>
                </th>
              </tr>
            </thead>
            <tbody className="text-right">
              <tr>
                <th rowSpan={4} className="text-left">
                  Livestock emissions
                </th>
                <th scope="row" className="text-left">
                  Dairy cattle (incl. grazing dairy)
                </th>
                <td>{formatNumber(livestock_emissions.DAIRY.kg_co2e, 0)}</td>
                <td className="table-cell-space-left bg-light"></td>
                <td>{formatNumber(livestock_emissions.DAIRY.kg_of_ch4, 0)}</td>
                <td>{formatNumber(livestock_emissions.DAIRY.kg_of_n2o, 0)}</td>
              </tr>
              <tr>
                <th scope="row" className="text-left">
                  Beef cattle
                </th>
                <td>{formatNumber(livestock_emissions.BEEF.kg_co2e, 0)}</td>
                <td className="table-cell-space-left bg-light"></td>
                <td>{formatNumber(livestock_emissions.BEEF.kg_of_ch4, 0)}</td>
                <td>{formatNumber(livestock_emissions.BEEF.kg_of_n2o, 0)}</td>
              </tr>
              <tr>
                <th scope="row" className="text-left">
                  Sheep
                </th>
                <td>{formatNumber(livestock_emissions.SHEEP.kg_co2e, 0)}</td>
                <td className="table-cell-space-left bg-light"></td>
                <td>{formatNumber(livestock_emissions.SHEEP.kg_of_ch4, 0)}</td>
                <td>{formatNumber(livestock_emissions.SHEEP.kg_of_n2o, 0)}</td>
              </tr>
              <tr>
                <th scope="row" className="text-left">
                  Deer
                </th>
                <td>{formatNumber(livestock_emissions.DEER.kg_co2e, 0)}</td>
                <td className="table-cell-space-left bg-light"></td>
                <td>{formatNumber(livestock_emissions.DEER.kg_of_ch4, 0)}</td>
                <td>{formatNumber(livestock_emissions.DEER.kg_of_n2o, 0)}</td>
              </tr>
              <tr>
                <th rowSpan={5} className="text-left">
                  Fertiliser and lime use
                </th>
                <th scope="row" className="text-left">
                  Non-urea nitrogen fertiliser
                </th>
                <td>
                  {formatNumber(fertiliser_and_lime_use.non_urea_nitrogen_fertiliser.kg_co2e, 0)}
                </td>
                <td className="table-cell-space-left bg-light"></td>
                <td className="bg-light"></td>
                <td>
                  {formatNumber(fertiliser_and_lime_use.non_urea_nitrogen_fertiliser.kg_of_n2o, 0)}
                </td>
              </tr>
              <tr>
                <th scope="row" className="text-left">
                  Urea without urease inhibitor
                </th>
                <td>
                  {formatNumber(fertiliser_and_lime_use.urea_without_urease_inhibitor.kg_co2e, 0)}
                </td>
                <td className="table-cell-space-left">
                  {formatNumber(fertiliser_and_lime_use.urea_without_urease_inhibitor.kg_of_co2, 0)}
                </td>
                <td className="bg-light"></td>
                <td>
                  {formatNumber(fertiliser_and_lime_use.urea_without_urease_inhibitor.kg_of_n2o, 0)}
                </td>
              </tr>
              <tr>
                <th scope="row" className="text-left">
                  Urea with urease inhibitor
                </th>
                <td>
                  {formatNumber(fertiliser_and_lime_use.urea_with_urease_inhibitor.kg_co2e, 0)}
                </td>
                <td className="table-cell-space-left text-right">
                  {formatNumber(fertiliser_and_lime_use.urea_with_urease_inhibitor.kg_of_co2, 0)}
                </td>
                <td className="bg-light"></td>
                <td>
                  {formatNumber(fertiliser_and_lime_use.urea_with_urease_inhibitor.kg_of_n2o, 0)}
                </td>
              </tr>
              <tr>
                <th scope="row" className="text-left">
                  Limestone
                </th>
                <td>{formatNumber(fertiliser_and_lime_use.limestone.kg_co2e, 0)}</td>
                <td className="table-cell-space-left text-right">
                  {formatNumber(fertiliser_and_lime_use.limestone.kg_of_co2, 0)}
                </td>
                <td className="bg-light"></td>
                <td className="bg-light"></td>
              </tr>
              <tr>
                <th scope="row" className="text-left">
                  Dolomite
                </th>
                <td>{formatNumber(fertiliser_and_lime_use.dolomite.kg_co2e, 0)}</td>
                <td className="table-cell-space-left text-right">
                  {formatNumber(fertiliser_and_lime_use.dolomite.kg_of_co2, 0)}
                </td>
                <td className="bg-light"></td>
                <td className="bg-light"></td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td rowSpan={11} className="cell-no-border"></td>
                <th scope="row" className="text-right">
                  <strong>Total kg</strong>
                </th>
                <td className="text-right">
                  <strong>{formatNumber(farm_emissions.totals.total_kg, 0)}</strong>
                </td>
                <td className="table-cell-space-left text-right">
                  <strong>{formatNumber(farm_emissions.totals.co2_total_kg, 0)}</strong>
                </td>
                <td className="text-right">
                  <strong>{formatNumber(farm_emissions.totals.ch4_total_kg, 0)}</strong>
                </td>
                <td className="text-right">
                  <strong>{formatNumber(farm_emissions.totals.n2o_total_kg, 0)}</strong>
                </td>
              </tr>
              <tr>
                <th scope="row" className="text-right">
                  <strong>Kg / total ha</strong>
                </th>
                <td className="text-right">
                  <strong>{formatNumber(farm_emissions.totals.total_kg_by_total_ha, 0)}</strong>
                </td>
                <td className="table-cell-space-left text-right">
                  <strong>{formatNumber(farm_emissions.totals.co2_total_kg_by_total_ha, 0)}</strong>
                </td>
                <td className="text-right">
                  <strong>{formatNumber(farm_emissions.totals.ch4_total_kg_by_total_ha, 0)}</strong>
                </td>
                <td className="text-right">
                  <strong>{formatNumber(farm_emissions.totals.n2o_total_kg_by_total_ha, 0)}</strong>
                </td>
              </tr>
              <tr>
                <td colSpan={999} className="cell-no-border"></td>
              </tr>
              <tr>
                <th scope="row" className="bg-light">
                  <Tippy
                    interactive
                    content={
                      <span className="text-left">
                        This benchmarking data has been derived from the Sheep and Beef Farm Survey,
                        and displayed based on the season, Production region and Farm class you've
                        selected on the 'Your farm' screen.
                      </span>
                    }
                  >
                    <strong>
                      Production region &amp; Farm class
                      <TooltipIcon />
                      <br />
                      average emissions (kg / total ha)
                    </strong>
                  </Tippy>
                </th>
                <td className="text-right bg-light">
                  <strong>{formatNumber(benchmark_emissions.kg_co2e_per_ha, 0)}</strong>
                </td>
                <td className="table-cell-space-left text-right bg-light">
                  <strong>{formatNumber(benchmark_emissions.kg_co2_per_ha, 0)}</strong>
                </td>
                <td className="text-right bg-light">
                  <strong>{formatNumber(benchmark_emissions.kg_ch4_per_ha, 0)}</strong>
                </td>
                <td className="text-right bg-light">
                  <strong>{formatNumber(benchmark_emissions.kg_n2o_per_ha, 0)}</strong>
                </td>
              </tr>
              <tr>
                <td colSpan={999} className="cell-no-border"></td>
              </tr>
              <tr>
                <th colSpan={2} className="text-left">
                  <strong>Tonnes of carbon dioxide equivalents {CO2e}</strong>
                </th>
                <th className="table-cell-space-left text-left" colSpan={3}>
                  <strong>Tonnes of greenhouse gases</strong>
                </th>
              </tr>
              <tr>
                <th>Carbon dioxide {CO2}</th>
                <td className="text-right">
                  {formatNumber(farm_emissions.totals.co2_total_tonnes, 0)}
                </td>
                <th className="table-cell-space-left">Carbon dioxide {CO2}</th>
                <th>Methane {CH4}</th>
                <th>Nitrous oxide {N2O}</th>
              </tr>
              <tr>
                <th>
                  Methane {CH4} (tonnes {CH4} x {farm_emissions.ch4_potential})
                </th>
                <td className="text-right">
                  {formatNumber(farm_emissions.totals.co2e_ch4_tonnes, 0)}
                </td>

                <td className="table-cell-space-left text-right">
                  {formatNumber(farm_emissions.totals.co2_total_tonnes, 0)}
                </td>
                <td className="text-right">
                  {formatNumber(farm_emissions.totals.ch4_total_tonnes, 0)}
                </td>
                <td className="text-right">
                  {formatNumber(farm_emissions.totals.n2o_total_tonnes, 0)}
                </td>
              </tr>
              <tr>
                <th>
                  Nitrous oxide {N2O} (tonnes {N2O} x {farm_emissions.n2o_potential})
                </th>
                <td className="text-right">
                  {formatNumber(farm_emissions.totals.co2e_n2o_tonnes, 0)}
                </td>
                <td colSpan={3} className="cell-no-border"></td>
              </tr>
              <tr>
                <th scope="row" className="text-right">
                  <strong>Tonnes (X)</strong>
                </th>
                <td className="text-right">
                  <strong>{formatNumber(farm_emissions.totals.total_tonnes, 0)}</strong>
                </td>
                <td colSpan={3} className="cell-no-border"></td>
              </tr>
              <tr>
                <th scope="row" className="text-right">
                  <strong>Tonnes / total ha</strong>
                </th>
                <td className="text-right">
                  <strong>{formatNumber(farm_emissions.totals.total_tonnes_by_total_ha, 2)}</strong>
                </td>
                <td colSpan={3} className="cell-no-border"></td>
              </tr>
            </tfoot>
          </Table>
        </div>
      </div>
      <div className="row">
        <div className="col-lg-8">
          <Table bordered className="table-ghg-results">
            <caption>
              <strong>Deforestation emissions</strong>
            </caption>
            <thead>
              <tr>
                <th colSpan={2}>
                  <strong>Source</strong>
                </th>
                <th className="text-center w-20">
                  <strong>Kilograms of Carbon dioxide equivalents {CO2e}</strong>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">Exotic forest</th>
                <th>Harvest and deforestation</th>
                <td className="text-right">
                  {formatNumber(
                    deforestation_emissions.exotic_forest.harvest_and_deforestation_kg_co2e,
                    0
                  )}
                </td>
              </tr>
              <tr>
                <th scope="row">Indigenous forest</th>
                <th>Established or regenerated indigenous forest deforested</th>
                <td className="text-right">
                  {formatNumber(
                    deforestation_emissions.indigenous_forest.natural_forest_deforestation_kg_co2e,
                    0
                  )}
                </td>
              </tr>
              <tr>
                <th scope="row">Shrubland</th>
                <th>Established or regenerated shrubland deforested</th>
                <td className="text-right">
                  {formatNumber(deforestation_emissions.shrubland.mature_shrubland_loss_kg_co2e, 0)}
                </td>
              </tr>
            </tbody>
            <tfoot className="text-right">
              <tr>
                <td rowSpan={2} className="cell-no-border"></td>
                <th scope="row">
                  <strong>Total kg</strong>
                </th>
                <td>
                  <strong>{formatNumber(deforestation_emissions.totals.total_kg, 0)}</strong>
                </td>
              </tr>
              <tr>
                <th scope="row">
                  <strong>Tonnes (Y)</strong>
                </th>
                <td>
                  <strong>{formatNumber(deforestation_emissions.totals.total_tonnes, 0)}</strong>
                </td>
              </tr>
            </tfoot>
          </Table>
          <Table bordered className="table-ghg-results">
            <caption>
              <strong>Vegetation offsets</strong>
            </caption>
            <thead>
              <tr>
                <th colSpan={2}>
                  <strong>Source</strong>
                </th>
                <th className="text-center w-20">
                  <strong>Kilograms of Carbon dioxide equivalents {CO2e}</strong>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">Exotic forest</th>
                <th>28 years of carbon storage, averaged per year</th>
                <td className="text-right">
                  {formatNumber(vegetation_offsets.exotic_forest.growth_offset_kg_co2e, 0)}
                </td>
              </tr>
              <tr>
                <th scope="row">Indigenous forest</th>
                <th>
                  Combined total carbon stock for regenerating (less than 100 years old) and
                  established (more than 100 years old) indigenous forests
                </th>
                <td className="text-right">
                  {formatNumber(
                    vegetation_offsets.indigenous_forest
                      .regenerating_and_tall_natural_forest_offset_kg_co2e,
                    0
                  )}
                </td>
              </tr>
              <tr>
                <th scope="row">Shrubland</th>
                <th>
                  Total carbon stock associated with shrubland older and younger than 30 years old
                </th>
                <td className="text-right">
                  {formatNumber(vegetation_offsets.shrubland.all_shrubland_offset_kg_co2e, 0)}
                </td>
              </tr>
            </tbody>
            <tfoot className="text-right">
              <tr>
                <td rowSpan={2} className="cell-no-border"></td>
                <td scope="row">
                  <strong>Total kg</strong>
                </td>
                <td>
                  <strong>{formatNumber(vegetation_offsets.totals.total_kg, 0)}</strong>
                </td>
              </tr>
              <tr>
                <td scope="row">
                  <strong>Tonnes (Z)</strong>
                </td>
                <td>
                  <strong>{formatNumber(vegetation_offsets.totals.total_tonnes, 0)}</strong>
                </td>
              </tr>
            </tfoot>
          </Table>
          <Table bordered className="ghg-results-summary d-table">
            <tbody>
              <tr>
                <td className="d-table-cell">
                  Estimated net {CO2} emissions (t {CO2e}) = X + Y - Z
                </td>
                <td className="d-table-cell text-right w-20">
                  {formatNumber(totals.estimated_total_tonnes, 0)}
                </td>
              </tr>
              <tr>
                <td className="d-table-cell">
                  Estimated net {CO2} emissions / total ha (t {CO2e})
                </td>
                <td className="d-table-cell text-right w-20">
                  {formatNumber(totals.estimated_total_tonnes_by_total_ha, 2)}
                </td>
              </tr>
            </tbody>
          </Table>
          <p>
            <sup>*</sup> {CO2e} (Carbon dioxide equivalents) are calculated using GWP100 (global
            warming potential) from the IPCC, 2007, Fourth Assessment Report
          </p>
        </div>
      </div>
    </>
  )
}
